import React from "react";
import { Share2, Link2 } from "lucide-react";
import { toast } from "sonner";
import { usePrefs } from "@/contexts/PrefsContext";
import type { Booking } from "@/types";
import { fmtDay } from "@/lib/time";

const WaIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
    <path d="M17.5 14.4c-.3-.1-1.8-.9-2-1s-.5-.1-.7.1-.8 1-1 1.2-.4.2-.7.1a8.2 8.2 0 0 1-4-3.5c-.3-.5.3-.5.9-1.6.1-.2 0-.4 0-.5l-1-2.3c-.2-.6-.5-.5-.7-.5h-.6a1.2 1.2 0 0 0-.9.4 3.6 3.6 0 0 0-1.1 2.7 6.3 6.3 0 0 0 1.3 3.3 14.4 14.4 0 0 0 5.5 4.9c2 .9 2.8 1 3.9.8a3.3 3.3 0 0 0 2.1-1.5 2.7 2.7 0 0 0 .2-1.5c-.1-.1-.3-.2-.6-.3zM12 21.8a9.8 9.8 0 0 1-5-1.4l-.4-.2-3.7 1 1-3.6-.2-.4A9.8 9.8 0 1 1 12 21.8zM12 0a12 12 0 0 0-10.3 18L0 24l6.2-1.6A12 12 0 1 0 12 0z" />
  </svg>
);

// Server page with the event poster/title preview; it forwards people to /events/{id}
export const eventShareLink = (id: string) => `${window.location.origin}/api/share/events/${id}`;
export const venueShareLink = (id: string) => `${window.location.origin}/api/share/venues/${id}`;

export const shareText = (b: Booking, t: (k: string) => string, lang: string) => {
  const link = b.event_id ? eventShareLink(b.event_id) : venueShareLink(b.venue_id);
  const msg = t("share.text")
    .replace("{venue}", lang === "ar" ? b.venue_name_ar : b.venue_name_en)
    .replace("{date}", fmtDay(b.date, lang))
    .replace("{guests}", String(b.guests));
  return { msg, link };
};

export const EventShareButtons = ({ eventId, title, date }: { eventId: string; title: string; date: string }) => {
  const { t } = usePrefs();
  return <LinkShareButtons id="event-share" link={eventShareLink(eventId)} msg={t("share.event").replace("{title}", title).replace("{date}", date)} />;
};

export const VenueShareButtons = ({ venueId, name }: { venueId: string; name: string }) => {
  const { t } = usePrefs();
  return <LinkShareButtons id="venue-share" link={venueShareLink(venueId)} msg={t("share.venue").replace("{name}", name)} />;
};

export const LinkShareButtons = ({ id, link, msg }: { id: string; link: string; msg: string }) => {
  const { t } = usePrefs();
  const btn = "h-11 px-4 rounded-full font-bold text-sm inline-flex items-center justify-center gap-1.5 active:scale-95 transition-transform";
  return (
    <div className="flex flex-wrap gap-2" data-testid={id}>
      <a href={`https://wa.me/?text=${encodeURIComponent(`${msg}\n${link}`)}`} target="_blank" rel="noreferrer" data-testid={`${id}-whatsapp`} className={`${btn} bg-[#25D366] text-white hover:bg-[#1ebe5b]`}>
        <WaIcon />{t("share.whatsapp")}
      </a>
      {typeof navigator !== "undefined" && !!navigator.share ? (
        <button data-testid={`${id}-native`} onClick={() => navigator.share({ title: t("appName"), text: msg, url: link }).catch(() => {})} className={`${btn} bg-accent`}>
          <Share2 className="h-4 w-4" />{t("share.more")}
        </button>
      ) : (
        <button data-testid={`${id}-copy`} onClick={() => { navigator.clipboard?.writeText(link); toast.success(t("share.copied")); }} className={`${btn} bg-accent`}>
          <Link2 className="h-4 w-4" />{t("share.copyLink")}
        </button>
      )}
    </div>
  );
};

export const ShareTiles = ({ b, tile }: { b: Booking; tile: string }) => {
  const { t, lang } = usePrefs();
  const { msg, link } = shareText(b, t, lang);
  const canNative = typeof navigator !== "undefined" && !!navigator.share;
  return (
    <>
      <a href={`https://wa.me/?text=${encodeURIComponent(`${msg}\n${link}`)}`} target="_blank" rel="noreferrer" data-testid={`ticket-share-whatsapp-${b.booking_id}`}
        className={`${tile} !bg-[#25D366] !text-white`}>
        <WaIcon /><span>{t("share.waShort")}</span>
      </a>
      {canNative && (
        <button data-testid={`ticket-share-native-${b.booking_id}`} onClick={() => navigator.share({ title: t("appName"), text: msg, url: link }).catch(() => {})} className={tile}>
          <Share2 className="h-5 w-5" /><span>{t("share.more")}</span>
        </button>
      )}
    </>
  );
};

export const ShareButtons = ({ b, compact = false }: { b: Booking; compact?: boolean }) => {
  const { t, lang } = usePrefs();
  const { msg, link } = shareText(b, t, lang);
  const wa = `https://wa.me/?text=${encodeURIComponent(`${msg}\n${link}`)}`;
  const canNative = typeof navigator !== "undefined" && !!navigator.share;
  const base = compact ? "h-9 px-3 text-xs" : "h-11 px-4 text-sm";
  return (
    <div className={`flex gap-2 ${compact ? "" : "w-full"}`}>
      <a
        href={wa}
        target="_blank"
        rel="noreferrer"
        data-testid={`${compact ? "booking" : "ticket"}-share-whatsapp-${b.booking_id}`}
        className={`${base} ${compact ? "" : "flex-1"} rounded-full bg-[#25D366] text-white font-bold inline-flex items-center justify-center gap-1.5 hover:bg-[#1ebe5b] active:scale-95 transition-[transform,background-color]`}
      >
        <WaIcon />
        {compact ? "" : t("share.whatsapp")}
      </a>
      {canNative && !compact && (
        <button
          data-testid={`ticket-share-native-${b.booking_id}`}
          onClick={() => navigator.share({ title: t("appName"), text: msg, url: link }).catch(() => {})}
          className={`${base} rounded-full bg-accent font-bold inline-flex items-center gap-1.5`}
        >
          <Share2 className="h-4 w-4" />
          {t("share.more")}
        </button>
      )}
    </div>
  );
};
